import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { getUserProfile } from '../../../services/userService';

/**
 * ProtectedRoute Component
 *
 * Handles authentication-based routing:
 * 1. Redirects unauthenticated users from protected routes to login
 * 2. Redirects authenticated users from auth routes to chat
 * 3. Handles token refresh when needed
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} [props.children] - Child components (optional)
 * @param {boolean} [props.authPage] - Flag to indicate authentication page
 * @returns {React.ReactElement} Redirected or original component
 */
interface ProtectedRouteProps {
  children?: React.ReactNode;
  authPage?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  authPage = false
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if we have tokens stored
  const checkTokens = () => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    return {
      hasTokens: !!(token && refreshToken && token !== 'undefined' && refreshToken !== 'undefined'),
      token,
      refreshToken
    };
  };

  useEffect(() => {
    const validateAuth = async () => {
      const { hasTokens } = checkTokens();

      if (!hasTokens) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        // Verify current token with a protected endpoint
        await getUserProfile();
        setIsAuthenticated(true);
      } catch {
        // Token refresh is handled by axios interceptor
        // If we get here, both current token and refresh failed
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateAuth();
  }, []);

  // Handle storage changes for cross-tab synchronization
  useEffect(() => {
    const handleStorageChange = () => {
      const { hasTokens } = checkTokens();
      setIsAuthenticated(hasTokens);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (isLoading) {
    return null; // Or a loading spinner component
  }

  // If it's an auth page and user is authenticated, redirect to chat
  // BUT check for pending invite token first
  if (authPage && isAuthenticated) {
    const pendingInviteToken = localStorage.getItem('pendingInviteToken');
    if (pendingInviteToken) {
      return <Navigate to="/accept-invite" />;
    }
    return <Navigate to="/chat" />;
  }

  // If it's a protected route and not authenticated, redirect to login
  if (!authPage && !isAuthenticated) {
    return <Navigate to="/" />;
  }

  // Render children for both authenticated routes and unauthenticated auth pages
  return children ? <>{children}</> : null;
};
